import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

const TRIVIA_QUESTIONS = [
  {
    question: "What's the recommended amount to save in your emergency fund?",
    options: [
      "1-2 months of expenses",
      "3-6 months of expenses",
      "6-12 months of expenses",
      "1 year of expenses",
    ],
    correctAnswer: 1,
    explanation:
      "Financial experts recommend saving 3-6 months of expenses in your emergency fund to cover unexpected costs.",
  },
  {
    question: "What's the best way to improve your credit score?",
    options: [
      "Close old credit cards",
      "Pay bills on time and keep balances low",
      "Apply for multiple credit cards",
      "Ignore your credit report",
    ],
    correctAnswer: 1,
    explanation:
      "Paying bills on time and keeping credit card balances low are key factors in maintaining a good credit score.",
  },
  {
    question: "What's the 50/30/20 rule in budgeting?",
    options: [
      "50% savings, 30% wants, 20% needs",
      "50% needs, 30% wants, 20% savings",
      "50% wants, 30% needs, 20% savings",
      "50% needs, 30% savings, 20% wants",
    ],
    correctAnswer: 1,
    explanation:
      "The 50/30/20 rule suggests spending 50% on needs, 30% on wants, and saving 20% of your income.",
  },
  {
    question: "What's compound interest?",
    options: [
      "Interest earned only on the principal amount",
      "Interest earned on both principal and accumulated interest",
      "A type of loan interest",
      "A bank fee",
    ],
    correctAnswer: 1,
    explanation:
      "Compound interest is interest earned on both the initial principal and the accumulated interest from previous periods.",
  },
  {
    question: "What's the main benefit of a high-yield savings account?",
    options: [
      "No minimum balance required",
      "Higher interest rates than regular savings",
      "Unlimited withdrawals",
      "No fees",
    ],
    correctAnswer: 1,
    explanation:
      "High-yield savings accounts offer higher interest rates than traditional savings accounts, helping your money grow faster.",
  },
];

const FinancialTriviaChallenge = () => {
  const theme = useTheme();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [gameOver, setGameOver] = useState(false);

  const handleAnswerSelect = (event) => {
    setSelectedAnswer(Number(event.target.value));
  };

  const handleSubmit = () => {
    if (selectedAnswer === TRIVIA_QUESTIONS[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
    setShowExplanation(true);
  };

  const handleNext = () => {
    if (currentQuestion < TRIVIA_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setGameOver(true);
    }
  };

  const handleReset = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setScore(0);
    setShowExplanation(false);
    setGameOver(false);
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #F0F7FF 0%, #E6F3FF 100%)",
        border: "4px solid #1E40AF",
        borderRadius: "24px",
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 320,
        maxWidth: 500,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "url('/credit-union-logo-watermark.png')",
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.05,
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        variant="h5"
        sx={{
          mb: 2,
          color: "#1E40AF",
          fontWeight: 700,
          textAlign: "center",
          fontSize: "1.5rem",
        }}
      >
        Financial Trivia Challenge
      </Typography>

      {!gameOver ? (
        <>
          <Typography
            sx={{
              color: "#2563EB",
              fontWeight: 600,
              mb: 2,
              textAlign: "center",
            }}
          >
            Question {currentQuestion + 1} of {TRIVIA_QUESTIONS.length}
          </Typography>

          <Typography
            sx={{
              color: "#1E40AF",
              fontWeight: 600,
              mb: 3,
              textAlign: "center",
              fontSize: "1.1rem",
            }}
          >
            {TRIVIA_QUESTIONS[currentQuestion].question}
          </Typography>

          <RadioGroup
            value={selectedAnswer}
            onChange={handleAnswerSelect}
            sx={{ width: "100%", mb: 3 }}
          >
            {TRIVIA_QUESTIONS[currentQuestion].options.map((option, index) => {
              const isCorrect =
                index === TRIVIA_QUESTIONS[currentQuestion].correctAnswer;
              const isSelected = selectedAnswer === index;
              const showFeedback = showExplanation;

              let backgroundColor = "white";
              let borderColor = "#E2E8F0";

              if (showFeedback) {
                if (isCorrect) {
                  backgroundColor = "#D1FAE5";
                  borderColor = "#059669";
                } else if (isSelected) {
                  backgroundColor = "#FEE2E2";
                  borderColor = "#DC2626";
                }
              }

              return (
                <FormControlLabel
                  key={index}
                  value={index}
                  control={<Radio />}
                  label={option}
                  disabled={showFeedback}
                  sx={{
                    mb: 1,
                    p: 1,
                    borderRadius: "8px",
                    backgroundColor,
                    border: `1px solid ${borderColor}`,
                    "&:hover": {
                      backgroundColor: showFeedback
                        ? backgroundColor
                        : "#F8FAFC",
                    },
                    "&.Mui-checked": {
                      backgroundColor: showFeedback
                        ? backgroundColor
                        : "#DBEAFE",
                      borderColor: showFeedback ? borderColor : "#1E40AF",
                    },
                    "&.Mui-disabled": {
                      opacity: 1,
                      color: showFeedback
                        ? isCorrect
                          ? "#059669"
                          : isSelected
                          ? "#DC2626"
                          : "#4B5563"
                        : "#4B5563",
                    },
                  }}
                />
              );
            })}
          </RadioGroup>

          {showExplanation ? (
            <Box sx={{ width: "100%", mb: 3 }}>
              <Typography
                sx={{
                  color:
                    selectedAnswer ===
                    TRIVIA_QUESTIONS[currentQuestion].correctAnswer
                      ? "#059669"
                      : "#DC2626",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                {selectedAnswer ===
                TRIVIA_QUESTIONS[currentQuestion].correctAnswer
                  ? "Correct! 🎉"
                  : "Not quite! 😕"}
              </Typography>
              <Typography
                sx={{
                  color: "#4B5563",
                  fontSize: "0.9rem",
                  backgroundColor: "white",
                  p: 2,
                  borderRadius: "8px",
                  border: "1px solid #E2E8F0",
                }}
              >
                {TRIVIA_QUESTIONS[currentQuestion].explanation}
              </Typography>
            </Box>
          ) : null}

          <Button
            variant="contained"
            onClick={showExplanation ? handleNext : handleSubmit}
            disabled={selectedAnswer === null}
            sx={{
              background: "#1E40AF",
              color: "#fff",
              fontWeight: 700,
              borderRadius: "100px",
              px: 4,
              mt: 1,
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              fontSize: "1.1rem",
              "&:hover": {
                background: "#2563EB",
                color: "#fff",
              },
              "&.Mui-disabled": {
                background: "#E2E8F0",
                color: "#94A3B8",
              },
            }}
          >
            {showExplanation
              ? currentQuestion < TRIVIA_QUESTIONS.length - 1
                ? "Next Question"
                : "See Results"
              : "Check Answer"}
          </Button>
        </>
      ) : (
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              color: "#1E40AF",
              fontWeight: 700,
              fontSize: "1.5rem",
              mb: 2,
            }}
          >
            Quiz Complete! 🎉
          </Typography>
          <Typography
            sx={{
              color: "#4B5563",
              fontSize: "1.1rem",
              mb: 3,
            }}
          >
            Your Score: {score} out of {TRIVIA_QUESTIONS.length}
          </Typography>
          <Button
            variant="contained"
            onClick={handleReset}
            sx={{
              background: "#1E40AF",
              color: "#fff",
              fontWeight: 700,
              borderRadius: "100px",
              px: 4,
              mt: 1,
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              fontSize: "1.1rem",
              "&:hover": {
                background: "#2563EB",
                color: "#fff",
              },
            }}
          >
            Try Again
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FinancialTriviaChallenge;
